import { useForm } from 'react-hook-form'
import { GoogleLogo } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { CardPageLayout, CardPageLegalLinks } from '@/components/card-page-layout/CardPageLayout'
import InputFieldFloatingLabel from '@/components/input-field-floating-label/InputFieldFloatingLabel'

type SignInFormInputs = {
  email: string
  password: string
}

type SignInPageContentProps = {
  /** Shown under the password field after a failed sign-in. */
  errorMessage: string | null
  submitting: boolean
  onSignIn: (email: string, password: string) => void
  onContinueWithGoogle: () => void
  onForgotPassword: () => void
  onCreateAccount: () => void
}

export function SignInPageContent({ errorMessage, submitting, onSignIn, onContinueWithGoogle, onForgotPassword, onCreateAccount }: SignInPageContentProps) {
  const { register, handleSubmit } = useForm<SignInFormInputs>({ defaultValues: { email: '', password: '' } })

  return (
    <CardPageLayout footer={<CardPageLegalLinks />}>
      <div className="flex flex-col gap-6">
        <h1 className="text-center font-rethink-sans text-xl font-bold leading-7 text-brand-neutral-950">Sign in</h1>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit((data) => onSignIn(data.email, data.password))}>
          <InputFieldFloatingLabel
            label="Work email address"
            id="email-address"
            required
            registration={register('email', { required: true })}
            inputProps={{ type: 'email', autoComplete: 'email', required: true }}
          />
          <InputFieldFloatingLabel
            label="Password"
            id="password"
            required
            error={errorMessage ?? undefined}
            registration={register('password', { required: true })}
            inputProps={{ type: 'password', autoComplete: 'current-password', required: true }}
          />
          <Button type="submit" fullWidth loading={submitting}>
            Sign in
          </Button>
        </form>

        <div className="flex items-center gap-3">
          <div aria-hidden="true" className="h-px flex-1 bg-brand-neutral-300" />
          <span className="text-sm font-medium text-brand-neutral-600">OR</span>
          <div aria-hidden="true" className="h-px flex-1 bg-brand-neutral-300" />
        </div>

        <Button variant="outlined" color="neutral" fullWidth leadingIcon={<GoogleLogo aria-hidden="true" />} onClick={onContinueWithGoogle}>
          Continue with Google
        </Button>

        <div className="flex flex-col gap-1">
          <p className="flex flex-wrap items-center justify-center gap-1 font-rethink-sans text-sm font-semibold text-brand-neutral-700">
            Forgot your password?
            <Button variant="plain" size="sm" onClick={onForgotPassword}>
              Send password reset email
            </Button>
          </p>
          <p className="flex flex-wrap items-center justify-center gap-1 font-rethink-sans text-sm font-semibold text-brand-neutral-700">
            Don’t have an account?
            <Button variant="plain" size="sm" onClick={onCreateAccount}>
              Create account
            </Button>
          </p>
        </div>
      </div>
    </CardPageLayout>
  )
}
